import React, { useEffect, useState, useRef } from 'react';
import { Configure, Index, useStats } from 'react-instantsearch';
import { baseConfig } from '../../config/algolia';
import { buildSearchFilters } from '@jbhi-fi/utils/src/buildAlgoliaSearchFilters/buildAlgoliaSearchFilters';
import { JBButton } from '@jbhi-fi/lanyard-ui';
import { ArrowRight } from '@jbhi-fi/lanyard-ui/components/Icons';
import { ProductSuggestions } from './ProductSuggestions';
import { KeywordSuggestions } from './KeywordSuggestions';
import CategorySuggestions from './CategorySuggestions';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import PropTypes from 'prop-types';
import { useFeatureFlag } from '../../hooks/useFeatureFlag/useFeatureFlag';

const DEBOUNCE_TIME = 500;
const { product_families_index, index_suggested_categories, index_suggested_keywords } = baseConfig;

const SaleItemStats = ({ setNbSaleItems }) => {
  const { nbHits } = useStats();

  useEffect(() => {
    setNbSaleItems(nbHits);
  }, [nbHits]);
  return <></>;
};
/**
 * Quick results component for search layover
 * @param {Function} onSearch Function passed down to initiate search
 * @param {Object} priceRange Object which contains the min/max values passed down (optional - if set in search layover)
 * @param {Function} setNbHits Function passed down to set the number of hits returned by Algolia
 * @param {Function} validateInput  Function passed down to initiate input validation
 */
const QuickResults = ({ onSearch, priceRange, setNbHits, validateInput, setNbSaleItems, saleItems, searchTerm }) => {
  const [statsFilter, setStatsFilter] = useState(`onPromotion:true`);
  const isMounted = useRef(false);
  const isSearchOverlayNewDesign = useFeatureFlag('isSearchOverlayNewDesign');
  const algoliaPersonalizationImpact = useFeatureFlag('algoliaPersonalizationImpact') ?? 0;
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      isMounted.current && setDebouncedSearchTerm(searchTerm);
    }, DEBOUNCE_TIME);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (priceRange && priceRange.min && priceRange.max) {
      setStatsFilter(`onPromotion:true AND price >= ${priceRange.min} AND price <= ${priceRange.max}`);
    } else {
      setStatsFilter(`onPromotion:true`);
    }
  }, [priceRange]);

  const getProductsFilter = () => {
    if (saleItems) return statsFilter;
    else return priceRange.min || priceRange.max ? `price >= ${priceRange.min} AND price <= ${priceRange.max}` : '';
  };
  /**
   * Render function
   */
  return isMounted.current ? (
    <div className={`search-quick-results ${isSearchOverlayNewDesign ? '' : 'old'}`}>
      <Index indexName={product_families_index} indexId="saleitems">
        <Configure filters={buildSearchFilters(statsFilter)} distinct query={debouncedSearchTerm} />
        <SaleItemStats setNbSaleItems={setNbSaleItems} />
      </Index>
      <div className="suggestions">
        <Index indexName={index_suggested_keywords}>
          <Configure clickAnalytics hitsPerPage={10} distinct query={debouncedSearchTerm} />
          <KeywordSuggestions onSearch={onSearch} priceRange={priceRange} saleItems={saleItems} />
        </Index>
        <Index indexName={index_suggested_categories}>
          <Configure clickAnalytics hitsPerPage={4} distinct query={debouncedSearchTerm} />
          <CategorySuggestions />
        </Index>
      </div>
      <Index indexId="quickresults" indexName={product_families_index}>
        <Configure
          clickAnalytics
          hitsPerPage={4}
          filters={buildSearchFilters(getProductsFilter())}
          distinct
          query={debouncedSearchTerm}
          {...(algoliaPersonalizationImpact > 0
            ? {
                personalizationImpact: algoliaPersonalizationImpact,
                enablePersonalization: true,
              }
            : {})}
        />
        <div className="product-suggestions">
          <div>
            <JBButton
              label="View all results"
              type="linkWithIcon"
              iconPosition="right"
              icon={<ArrowRight />}
              classes="btn-full-results"
              onClick={validateInput.bind(this)}
            />
          </div>
          <CalloutFont as="h3">Products</CalloutFont>

          <div className="product-suggestions--container">
            <ProductSuggestions setNbHits={setNbHits} priceRange={priceRange} saleItems={saleItems} />
          </div>
        </div>
      </Index>
    </div>
  ) : (
    <></>
  );
};

QuickResults.propTypes = {
  onSearch: PropTypes.func.isRequired,
  priceRange: PropTypes.object,
  setNbHits: PropTypes.func.isRequired,
  validateInput: PropTypes.func.isRequired,
  setNbSaleItems: PropTypes.func.isRequired,
  saleItems: PropTypes.bool,
  searchTerm: PropTypes.string,
};

export default QuickResults;
